import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/legacy-form-field';
import {
  MAT_LEGACY_SNACK_BAR_DEFAULT_OPTIONS as MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatLegacySnackBarModule as MatSnackBarModule,
} from '@angular/material/legacy-snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';

import { ApiModule } from '@assess/browser/api';
import { AuthenticationModule } from '@assess/browser/authentication';
import { ChimeraApiModule } from '@assess/browser/chimera-api';
import { dateFormatDefault, FILE_UPLOAD_CONFIG } from '@assess/browser/common-ui';
import { DataMartApiModule } from '@assess/browser/data-mart-api';
import {
  ErrorNotificationsInterceptor,
  SuccessNotificationsInterceptor,
  WITH_CREDENTIALS_CONFIG,
  WithCredentialsInterceptor,
} from '@assess/browser/http-interceptors';
import { HydraModule } from '@assess/browser/hydra';
import { Icd10CodeSearchModule } from '@assess/browser/icd10-code-search';
import { NewRelicModule } from '@assess/browser/new-relic';
import { PendoModule } from '@assess/browser/pendo';
import { BrowserWorkflowApiModule } from '@assess/browser/workflow-api';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { apiConfig } from './configs/api.config';
import { authConfig } from './configs/auth.config';
import { authenticationConfig } from './configs/authentication.config';
import { browserCommonUiConfig } from './configs/browser-common-ui.config';
import { chimeraApiConfig } from './configs/chimera-api.config';
import { dataMartApiConfig } from './configs/data-mart.config';
import { hydraConfig } from './configs/hydra.config';
import { icd10CodeSearch } from './configs/icd10-code-search.config';
import { matFormFieldConfig } from './configs/mat-form-field.config';
import { matSnackBarConfig } from './configs/mat-snack-bar.config';
import { newRelicConfig } from './configs/new-relic.config';
import { pendoConfig } from './configs/pendo.config';
import { workflowApiConfig } from './configs/workflow-api.config';
import { environment } from '../environments/environment';

@NgModule({
  bootstrap: [AppComponent],
  declarations: [AppComponent],
  imports: [
    MatNativeDateModule,
    ApiModule.forRoot(apiConfig),
    AppRoutingModule,
    AuthModule.forRoot(authConfig),
    AuthenticationModule.forRoot(authenticationConfig),
    BrowserModule,
    BrowserAnimationsModule,
    ChimeraApiModule.forRoot(chimeraApiConfig),
    DataMartApiModule.forRoot(dataMartApiConfig),
    Icd10CodeSearchModule.forRoot(icd10CodeSearch),
    BrowserWorkflowApiModule.forRoot(workflowApiConfig),
    HttpClientModule,
    HydraModule.forRoot({ ...hydraConfig, webSocketsEnabled: environment.production }),
    FormsModule,
    ReactiveFormsModule,
    StoreModule.forRoot({}, {}),
    EffectsModule.forRoot([]),
    MatSnackBarModule,
    MonacoEditorModule.forRoot(),
    NewRelicModule.forRoot(newRelicConfig),
    PendoModule.forRoot(pendoConfig),
    ...environment.plugins,
  ],
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: dateFormatDefault,
        },
        display: {
          dateInput: dateFormatDefault,
          monthYearLabel: 'LLL uuuu',
          dateA11yLabel: 'PP',
          monthYearA11yLabel: 'LLLL uuuu',
        },
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: matFormFieldConfig,
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: matSnackBarConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: ErrorNotificationsInterceptor,
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: SuccessNotificationsInterceptor,
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: WithCredentialsInterceptor,
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: AuthHttpInterceptor,
    },
    {
      provide: FILE_UPLOAD_CONFIG,
      useValue: browserCommonUiConfig.fileUploadConfig,
    },
    {
      provide: WITH_CREDENTIALS_CONFIG,
      useValue: browserCommonUiConfig.withCredentialsConfig,
    },
  ],
})
export class AppModule {}
